import React, { useEffect, useState, SyntheticEvent, useRef } from 'react';
import IkIslemleriAPI, { GetPersonelDetayRequest } from '../../../apis/IkIslemleriAPI';
import { Card, CardContent, Typography, Avatar, Box, Grid, Divider, Tabs, Tab, Paper, CircularProgress } from '@mui/material';
import SessionStorageService from "../../../services/StorageService";
import IzinVeRaporlar from "./IzinVeRaporlar";
import YillikIzinHakkedisleri from "./YillikIzinHakkedisleri";
import AileDurumBilgileri from "./AileDurumBilgileri";
import DemirbasZimmetListesi from "./DemirbasZimmetListesi";
import { useNavigate } from "react-router";
import AciklamaAndUserControlPopup from "../../../components/OB/popups/aciklama-usercontrol-popup";
import IzinGrafigi from "./IzinGrafigi";
import perSicilBilgiKontrol, { PerSicilBilgiKontrolRequest } from '../../../apis/MemurIzinTalepAPI';
import MemurIzinTalepAPI from '../../../apis/MemurIzinTalepAPI';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            <Box sx={{ p: 3 }}>
                {children}
            </Box>
        </div>
    );
}

const IkIslemleri = () => {
    const [personelDetay, setPersonelDetay] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const user = SessionStorageService.getUserInfo();
    const [activeTab, setActiveTab] = useState(0);
    const [resolvedPersonelTipi, setResolvedPersonelTipi] = useState<string | null>(null);
    const [userControlPopup, setUserControlPopup] = useState(false);
    const [izinTotals, setIzinTotals] = useState({ hakedis: 0, kullanilan: 0, kalan: 0 });

    // Sayfa yüklenme kontrolü - çift çağrıyı önlemek için (CRITICAL: Rate limiting)
    const isPageLoadedRef = useRef(false);

    const navigate = useNavigate();
    useEffect(() => {
        if (!user) {
            setUserControlPopup(true);
            setLoading(false);
        }
    }, [user]);

    const closeUserControlPopup = () => {
        setUserControlPopup(false);
        navigate(-1);
    };

    const handleChangeTab = (event: SyntheticEvent, newValue: number) => {
        setActiveTab(newValue);
    };

    const handleTotalsCalculated = (totals: { hakedis: number; kullanilan: number; kalan: number }) => {
        setIzinTotals(totals);
    };

    useEffect(() => {
        // StrictMode'da çift çağrıyı önle - CRITICAL: Rate limiting hatalarını önlemek için
        if (isPageLoadedRef.current) return;
        isPageLoadedRef.current = true;

        const fetchPersonelDetay = async () => {
            try {
                setLoading(true);

                // ADIM 1: Önce kimlik kontrolü yap
                const kontrolResponse = await MemurIzinTalepAPI.perSicilBilgiKontrol({
                    vatandaslikNo: user.tcKimlikNo
                });

                if (kontrolResponse.data.sonuc !== 0) {
                    setError(kontrolResponse.data.sonucAciklama);
                    return;
                }
                
                // personelTipleri = ['M', 'I', 'T', 'K'];
                // ctTur: memur:1  işçi,taşeron,kamu:2 
                // isciTaseron: işçi:1 taşeron,kamu:2
                let foundData = null;
                let foundPersonelTipi = null;
 
                const request: GetPersonelDetayRequest = {
                    sicilKodu: kontrolResponse.data.qtKurumSicilNo,
                    personelTipi: kontrolResponse.data.memurIsci
                };
                const response = await IkIslemleriAPI.getPersonelDetay(request);
                if (response && response.data && response.data.length > 0) {
                    foundData = response.data[0];
                    foundPersonelTipi = kontrolResponse.data.memurIsci;
                }
                
                if (foundData) {
                    setPersonelDetay(foundData);
                    setResolvedPersonelTipi(foundPersonelTipi); // Resolved tipi ayarla
                } else {
                    setError("Hiçbir personel tipinde veri bulunamadı.");
                }

            } catch (err: any) {
                setError("Veri çekilirken bir hata oluştu: " + err.message);
                console.error("Personel Detay Hatası:", err);
            } finally {
                setLoading(false);
            }
        };

        if (user && user.tcKimlikNo) {
            fetchPersonelDetay();
        } else if (!user) {
            setLoading(false);
        } else {
            setError("Kullanıcı bilgisi bulunamadı.");
            setLoading(false);
        }
    }, []);

    if (userControlPopup) {
        return (
            <AciklamaAndUserControlPopup
                open={userControlPopup}
                handleClose={closeUserControlPopup}
                isUserControlPopUp={true}
                description={"Bu sayfayı kullanmak için giriş yapmalısınız."}
            />
        );
    }

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="error">{error}</Typography>
            </Box>
        );
    }

    if (!personelDetay) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography>Personel detayları mevcut değil.</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Grid container justifyContent="center" columnSpacing={3}>
                <Grid item xs={12} sm={12} md={7} lg={6}>
                    <Card variant="outlined" sx={{ height: '100%' }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <Avatar
                                    alt="Personel Fotoğrafı"
                                    sx={{ width: 80, height: 80, mr: 2 }}
                                />
                                <Box>
                                    <Typography variant="h6" component="div">
                                        {`${personelDetay.adi || ''} ${personelDetay.soyadi || ''}`.trim()}
                                    </Typography>
                                    <Typography variant="subtitle1" color="text.secondary">
                                        {personelDetay.unvani}
                                    </Typography>
                                    <Typography variant="subtitle2" color="text.secondary">
                                        {personelDetay.gorevBirimIsyeriAdi}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {personelDetay.sicilKodu}
                                    </Typography>
                                </Box>
                            </Box>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={1}>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Görev Ünvanı:</strong> {personelDetay.unvani}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Görev Daire:</strong> {personelDetay.gorevBirimIsyeriAdi}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>E-mail:</strong> {personelDetay.mailAdresi}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Cep Tel.:</strong> {personelDetay.cepTelefonu}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Sendika:</strong> {personelDetay.sendikaAdi}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Kan Grubu:</strong> {personelDetay.kanGrubu}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Engel Oranı:</strong> {personelDetay.engelliOrani}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Derece/Kademe:</strong> {`AE ${personelDetay.ae || ''} EE ${personelDetay.ee || ''}`.trim()}
                                    </Typography>
                                </Grid>
                                <Grid item xs={6}>
                                    <Typography variant="body2">
                                        <strong>Hizmet Süresi:</strong> {personelDetay.hizmetSuresi}
                                    </Typography>
                                </Grid>
                                {personelDetay.ogretimDurumu && (
                                    <Grid item xs={6}>
                                        <Typography variant="body2">
                                            <strong>Öğrenim Durumu:</strong> {personelDetay.ogretimDurumu}
                                        </Typography>
                                    </Grid>
                                )}
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} sm={12} md={5} lg={3}>
                    <IzinGrafigi
                        kullanilan={izinTotals.kullanilan}
                        kalan={izinTotals.kalan}
                    />
                </Grid>
            </Grid>

            <Grid container justifyContent="center" sx={{ mt: 3 }}>
                <Grid item xs={12} sm={10} md={10}>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs value={activeTab} onChange={handleChangeTab} aria-label="personel detay sekmeleri" variant="fullWidth">
                            <Tab label="Kullanılan İzin ve Raporlar" />
                            <Tab label="Yıllık İzin Hakedişleri" />
                            <Tab label="Aile Durum Bilgileri" />
                            <Tab label="Zimmet Durum Bilgileri" />
                        </Tabs>
                    </Box>
                    <CustomTabPanel value={activeTab} index={0}>
                        <IzinVeRaporlar qtSicilId={personelDetay.qtSicilId} personelTipi={resolvedPersonelTipi || 'M'} />
                    </CustomTabPanel>
                    <CustomTabPanel value={activeTab} index={1}>
                        <YillikIzinHakkedisleri
                            qtSicilId={personelDetay.qtSicilId} personelTipi={resolvedPersonelTipi || 'M'} onTotalsCalculated={handleTotalsCalculated}
                        />
                    </CustomTabPanel>
                    <CustomTabPanel value={activeTab} index={2}>
                        <AileDurumBilgileri rfPerSicilBilgi={personelDetay.perSicilBilgiId} />
                    </CustomTabPanel>
                    <CustomTabPanel value={activeTab} index={3}>
                        <DemirbasZimmetListesi qtSicilId={personelDetay.qtSicilId} personelTipi={resolvedPersonelTipi || 'M'} />
                    </CustomTabPanel>
                </Grid>
            </Grid>
        </Box>
    );
};

export default IkIslemleri;